import { useState } from "react";
import { GiScrollUnfurled } from "react-icons/gi";
import { useBattles } from "../api/marches";
import { BattleLogEntry, BattleOutcome } from "../types/marches";
import { Loading } from "../pages/Loading";
import { BattleDetail } from "./BattleDetail";
import { hapticImpact } from "../lib/telegram";

interface Props {
  enabled: boolean;
  selfId: string | null;
  onBack: () => void;
}

export function BattleHistory({ enabled, selfId, onBack }: Props) {
  const battles = useBattles(enabled);
  const [openId, setOpenId] = useState<string | null>(null);

  const openBattle = openId
    ? (battles.data ?? []).find((b) => b.id === openId) ?? null
    : null;

  if (openBattle) {
    return (
      <BattleDetail
        battle={openBattle}
        selfId={selfId}
        onBack={() => setOpenId(null)}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium inline-flex items-center gap-1.5"><GiScrollUnfurled size={14} /> История боёв</div>
      </div>

      {battles.isPending ? (
        <Loading message="Поднимаю архив..." />
      ) : battles.isError ? (
        <div className="px-3 py-4 text-sm text-tg-destructive">
          Не получилось загрузить историю.
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
          {(battles.data ?? []).length === 0 ? (
            <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
              Пока без боёв.
            </div>
          ) : (
            (battles.data ?? []).map((b) => (
              <BattleRow
                key={b.id}
                battle={b}
                selfId={selfId}
                onOpen={() => {
                  hapticImpact("light");
                  setOpenId(b.id);
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BattleRow({
  battle,
  selfId,
  onOpen,
}: {
  battle: BattleLogEntry;
  selfId: string | null;
  onOpen: () => void;
}) {
  const kind: "pvp" | "pve" | "rally" = battle.kind ?? (battle.campId ? "pve" : "pvp");
  const wasAttacker = selfId != null && battle.attackerId === selfId;
  const won =
    (battle.outcome === BattleOutcome.TeamA && wasAttacker) ||
    (battle.outcome === BattleOutcome.TeamB && !wasAttacker);
  const isDraw = battle.outcome === BattleOutcome.Draw;

  const verdict = isDraw ? "Ничья" : won ? "Победа" : "Поражение";
  const verdictClass = isDraw
    ? "text-tg-hint"
    : won
    ? "text-tg-accent"
    : "text-tg-destructive";

  const date = new Date(battle.resolvedAt).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center justify-between rounded-xl bg-tg-secondaryBg p-3 text-left"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <KindChip kind={kind} />
          <span className="truncate">
            {kind === "rally"
              ? "Ралли альянса"
              : kind === "pve"
              ? "PvE атака"
              : wasAttacker
              ? "Моя атака"
              : "Атака на меня"}
          </span>
        </div>
        <div className="text-xs text-tg-hint">{date}</div>
      </div>
      <div className="flex items-center gap-2">
        <div className={`text-sm font-medium ${verdictClass}`}>{verdict}</div>
        <div className="text-tg-hint">›</div>
      </div>
    </button>
  );
}

// Compact colored chip showing the battle type. Colors stay readable on both
// Telegram themes — solid backgrounds with high-contrast text.
function KindChip({ kind }: { kind: "pvp" | "pve" | "rally" }) {
  const palette =
    kind === "rally"
      ? { bg: "bg-amber-500/20", text: "text-amber-400" }
      : kind === "pve"
      ? { bg: "bg-emerald-500/20", text: "text-emerald-400" }
      : { bg: "bg-rose-500/20", text: "text-rose-400" };
  const label = kind === "rally" ? "Ралли" : kind === "pve" ? "PvE" : "PvP";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${palette.bg} ${palette.text}`}>
      {label}
    </span>
  );
}
